import { faker } from '@faker-js/faker/locale/en_IN';
import _ from 'lodash';
import { defineStore } from 'pinia';
import { useConfsStore } from './confs';
import { useInfluencersStore } from './influencers';
import msApi from '@/utils/msApi';

export type MarketplaceMode = 'self-select' | 'influencer_attributes' | 'groups';

export interface IJobForm {
  brand_id: string;
  job_id?: string;
  name: string;
  bid_start_date: string;
  bid_end_date: string;
  budget: number | null;
  currency: string;
  country: string;
  channels: string[];
  estimated_campaign_start_date: string;
  campaign_period: 'day' | 'week' | 'month';
  campaign_duration: number | null;
  marketplace_mode: 'self-select' | 'influencer_attributes' | 'groups';
  marketplace_values_group?: string[];
  marketplace_values_self_select?: string[];
  marketplace_values_influencer_attributes: {
    occupation: string;
    interests: any[];
    age_group: string;
    posting_about: string[];
    countries: string;
    locations: any[];
    gender: 'MALE' | 'FEMALE' | 'BOTH';
    income_category: string;
    influencer_levels: any[];
  };
  tasks: { channel: string; post_content_type: string | null; number: number | any }[];
}

const emptyJobForm: IJobForm = {
  brand_id: '',
  name: '',
  bid_start_date: '',
  bid_end_date: '',
  budget: null,
  currency: '',
  country: '',
  estimated_campaign_start_date: '',
  campaign_period: 'day',
  campaign_duration: null,
  marketplace_mode: 'influencer_attributes',
  marketplace_values_group: [],
  marketplace_values_self_select: [],
  channels: [] as string[],
  marketplace_values_influencer_attributes: {
    occupation: '',
    interests: [],
    age_group: '',
    posting_about: [],
    countries: '',
    locations: [],
    gender: 'BOTH',
    income_category: '',
    influencer_levels: [],
  },
  tasks: [],
};

export const useJobsStore = defineStore('jobs', {
  state: () => ({
    jobForm: _.cloneDeep(emptyJobForm) as IJobForm,
    error: null as any | null,
    jobs: null as any,
    jobsLoading: true,
    searchValue: '',
    selectedJob: null as null | { name: any; influencers: any; tasks: any; id: string },
    cache: {} as Record<string, { data: any; timestamp: number }>,
    cacheConfig: {
      duration: 5 * 60 * 1000, // 5 minutes - adjust as needed
    },
  }),

  actions: {
    async doCreateJob(id: string, publish: boolean, job_id?: any) {
      const outputTask = this.jobForm.tasks.flatMap(item => {
        const count = parseInt(item.number, 10);
        return Array(count).fill({
          channel: item.channel,
          post_content_type: item.post_content_type,
        });
      });
      const data: any = {
        brand_id: id,
        name: this.jobForm.name,
        bid_start_date: this.jobForm.bid_start_date,
        bid_end_date: this.jobForm.bid_end_date,
        budget: parseInt(this.jobForm.budget as any),
        currency: this.jobForm.currency,
        estimated_campaign_start_date: this.jobForm.estimated_campaign_start_date,
        campaign_period: this.jobForm.campaign_period,
        campaign_duration: parseInt(this.jobForm.campaign_duration as any),
        marketplace_mode: this.jobForm.marketplace_mode,
        tasks: outputTask,
        publish: publish,
      };
      if (this.jobForm.marketplace_mode === 'influencer_attributes') {
        const influencerLevels: any = [];
        this.jobForm.channels.forEach(channel => {
          this.jobForm.marketplace_values_influencer_attributes.influencer_levels.forEach(level => {
            const obj: any = {};
            obj[channel] = level;
            influencerLevels.push(obj);
          });
        });

        data.marketplace_values_influencer_attributes = {
          occupation: this.jobForm.marketplace_values_influencer_attributes.occupation,
          interests: this.jobForm.marketplace_values_influencer_attributes.interests,
          age_group: this.jobForm.marketplace_values_influencer_attributes.age_group,
          posting_about: this.jobForm.marketplace_values_influencer_attributes.interests,
          countries: [this.jobForm.marketplace_values_influencer_attributes.countries],
          locations: this.jobForm.marketplace_values_influencer_attributes.locations,
          gender: this.jobForm.marketplace_values_influencer_attributes.gender, //Options: MALE, FEMALE, BOTH
          income_category: this.jobForm.marketplace_values_influencer_attributes.income_category,
          influencer_levels: influencerLevels,
        };
      } else if (this.jobForm.marketplace_mode === 'groups') {
        data.marketplace_values_group = this.jobForm.marketplace_values_group;
      } else if (this.jobForm.marketplace_mode === 'self-select') {
        const infStore = useInfluencersStore();
        data.marketplace_values_self_select = infStore.selectedInfluencers.map((item: any) => item.id);
      }
      if (job_id) {
        data.job_id = job_id;
      }
      this.error = null;
      this.jobsLoading = true;

      try {
        this.jobs = await msApi.msPost('/jobs-marketplace/job', data);
        this.jobsLoading = false;
        return this.jobs;
      } catch (e) {
        this.error = e;
      }
    },
    async doGetJobs(id: string, link?: string, platform?: string[], currency?: string, forceRefresh = false) {
      const cacheKey = `jobs${id}`;

      // Return cached data if valid and not forcing refresh
      if (!forceRefresh && this.isCacheValid(cacheKey)) {
        this.jobs = this.cache[cacheKey].data;
        return;
      }

      let url = '';
      if (link) {
        url = link;
      } else {
        url = '/jobs-marketplace/get_jobs';
      }

      this.error = null;
      this.jobsLoading = true;

      try {
        // Create request object with required brand_id
        const requestData: any = { brand_id: id };

        // Add platform to request data if provided
        if (platform) {
          requestData.platform = platform;
        }
        if (currency) {
          requestData.currency = currency;
        }

        const jobs = await msApi.msPost(url, requestData);

        this.jobs = jobs;

        // Cache the result
        this.cache[cacheKey] = {
          data: jobs,
          timestamp: Date.now(),
        };

        this.jobsLoading = false;
        return this.jobs;
      } catch (e) {
        this.error = e;
        this.jobsLoading = false;

        // Optionally return cached data on error if available
        if (this.cache[cacheKey]?.data) {
          this.jobs = this.cache[cacheKey].data;
          return this.jobs;
        }

        throw e;
      }
    },
    async doGetJobBids(data: { brand_id: string; job_id: string }) {
      this.error = null;
      try {
        const jobBids = await msApi.msPost('/jobs-marketplace/all_job_bids', data);
        return jobBids;
      } catch (e) {
        this.error = e;
      }
    },
    async doUpdateBid(data: { action: string; job_id: string; bid_id?: string; offer_total?: string }) {
      this.error = null;
      try {
        const updatedBid = await msApi.msPost('/jobs-marketplace/bid', data);
        return updatedBid;
      } catch (e) {
        this.error = e;
      }
    },
    async doUpdateJob(data: { action: string; job_id: string; brand_id: string; end_date?: string }) {
      this.error = null;
      try {
        const updatedJob = await msApi.msPost('/jobs-marketplace/update_job', data);

        // Clear cache for this brand since we updated a brief
        const cacheKey = `jobs${data.brand_id}`;
        delete this.cache[cacheKey];

        return updatedJob;
      } catch (e) {
        this.error = e;
      }
    },
    async resetForm() {
      this.jobForm = _.cloneDeep(emptyJobForm);
    },
  },

  getters: {
    isCacheValid: state => (cacheKey: string) => {
      const entry = state.cache[cacheKey];
      return entry?.data && entry?.timestamp && Date.now() - entry.timestamp < state.cacheConfig.duration;
    },

    periods() {
      return ['Days', 'Weeks', 'Months'].map(e => ({ value: e.toLowerCase().slice(0, e.length - 1), label: e }));
    },

    jobSelectionModes(): { value: MarketplaceMode; label: string; description: string }[] {
      return [
        {
          value: 'influencer_attributes',
          label: 'Open selection',
          description: 'Open the bid to every creator that matches your creator preference criteria',
        },
        {
          value: 'self-select',
          label: 'Self selection',
          description: 'Open the bid to a limited selected creator profiles ',
        },
        {
          value: 'groups',
          label: 'Creator group',
          description:
            'Open the bid to a curated custom group of creators (NB: ensure the creators in the group selected are active on the platform you want to run this bid on)',
        },
      ];
    },

    fakeJobs() {
      const states = ['Approved', 'Countered', 'Pending', 'Rejected'];
      const confsStore = useConfsStore();

      return Array.from({ length: 10 }).map((_, index) => ({
        id: index,
        name: faker.person.fullName(),
        avatar: faker.image.avatar(),
        location: 'NAIROBI',
        country: 'KE',
        channels: confsStore?.conf?.channels.map((e: any) => e.value).slice(0, 3),
        potential_reach: faker.number.int({ max: 100000 }),
        currency: 'KES',
        bid_price: faker.number.int({ max: 20000 }),
        status: states[faker.number.int({ min: 0, max: states.length - 1 })],
        date: faker.date.recent(),
      }));
    },

    fakeBids() {
      return Array.from({ length: 4 }).map(() => {
        return {
          current_brand_offer_total: null,
          current_brand_offer: null,
          currency: 'NGN',
          closed_by: null,
          status: 'ongoing',
          created: '2024-06-17 23:03:17.893044+01:00',
          bid_id: 'cbd8dbd79cb9cf69230290097553a76e',
          current_influencer_offer: [
            { channel: 'facebook', amount: '5000', post_content_type: 'Reel' },
            { channel: 'tiktok', amount: '5000', post_content_type: 'Live Video' },
            { channel: 'twitter', amount: '5000', post_content_type: 'Static Tweet' },
          ],
          job_id: '20240613115316681433dphinFIv7Y0eXrnK',
          current_influencer_offer_total: '15000',
          closed: false,
          history: Array.from({ length: 5 }).map((_, index) => ({
            offer: [
              { channel: 'facebook', amount: '5000', post_content_type: 'Reel' },
              { channel: 'tiktok', amount: '5000', post_content_type: 'Live Video' },
              { channel: 'twitter', amount: '5000', post_content_type: 'Static Tweet' },
            ],
            offer_total: '15000',
            role: index % 2 ? 'MERCHANT' : 'INFLUENCER',
            created: '2024-06-17 23:03:17.868087+01:00',
          })),
          updated: '2024-06-17 23:03:18.236122+01:00',
          closed_date: null,
          brand_id: '17345',
          influencer_id: '12343',
          closed_reason: null,
        };
      });
    },
  },
});
